import {useDisclosure} from '@mantine/hooks';
import type React from 'react';
import {useEffect, useRef} from 'react';
import {MdAddCircleOutline} from 'react-icons/md';
import {
  COLOR_PRIMARY,
  COLOR_WHITE,
} from '../../../../config/constants/colorCodes';
import {useAuth} from '../../../../core/usecases/auth/AuthContext';
import {useHandleFollowCompany} from '../../../../core/usecases/useHandleFollowCompany';
import {cn} from '../../../../lib/classname';
import {NoLoginAlertModal} from '../../../../shared/components/NoLoginAlertModal';
import {formatNumberAsKilo} from '../../../../shared/services/formatNumberAsKilo';

type Props = {
  companyId: number;
  initial: {
    followerCount: number;
    isFollowed: boolean;
  };
  hasPlus?: boolean;
  hasCount?: boolean;
  className?: string;
  isSmall?: boolean;
  dataGtmContent: string;
  /**
   * 未フォローからフォローへ遷移した成功時に呼ばれる。
   */
  onFollowSuccess?: () => void;
  /**
   * 同一ページ内の複数ボタンで状態を同期させたい場合に渡す。
   * 渡された場合は内部のフック状態ではなくこの値を表示し、
   * フォロー操作も onFollow に委譲する（controlled）。
   */
  controlled?: {
    isFollowed: boolean;
    followCount: number;
    onFollow: () => void | Promise<void>;
  };
};

export const FollowCompanyButton: React.FC<Props> = ({
  companyId,
  initial,
  hasPlus = true,
  hasCount = true,
  className,
  isSmall = false,
  dataGtmContent,
  controlled,
  onFollowSuccess,
}) => {
  const {status} = useAuth();
  const [opened, {open, close}] = useDisclosure(false);
  const internal = useHandleFollowCompany({
    companyId,
    followCount: initial.followerCount,
    followed: initial.isFollowed,
  });

  const isFollowed = controlled?.isFollowed ?? internal.isFollowed;

  const prevIsFollowedRef = useRef(isFollowed);
  useEffect(() => {
    if (!prevIsFollowedRef.current && isFollowed) {
      onFollowSuccess?.();
    }
    prevIsFollowedRef.current = isFollowed;
  }, [isFollowed, onFollowSuccess]);
  const followCountUpdated =
    controlled?.followCount ?? internal.followCountUpdated;
  const handleFollowCompany =
    controlled?.onFollow ?? internal.handleFollowCompany;

  const onClick = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (status === 'unauthenticated') {
      open();
      return;
    }

    return handleFollowCompany();
  };

  return (
    <>
      <button
        className={cn(
          'w-[150px] p-8 flex items-center justify-center gap-4 rounded-[20px] border-2 border-primary',
          className,
          {
            'bg-primary text-white': isFollowed,
            'bg-white text-primary': !isFollowed,
          },
        )}
        type="button"
        onClick={onClick}
        data-gtm-click="company-follow"
        data-gtm-content={dataGtmContent}
      >
        {hasPlus && (
          <MdAddCircleOutline
            color={isFollowed ? COLOR_WHITE : COLOR_PRIMARY}
            size="16px"
          />
        )}
        <span className="font-bold text-base">
          {isFollowed ? 'フォロー中' : 'フォロー'}
        </span>
        {hasCount && (
          <span
            className={cn({
              'text-xs': isSmall,
              'text-sm': !isSmall,
            })}
          >
            {formatNumberAsKilo(followCountUpdated)}
          </span>
        )}
      </button>
      <NoLoginAlertModal opened={opened} onClose={close} />
    </>
  );
};
